var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import '../icon/Icon.js';
const TYPE_STYLES = () => ({
    add: { label: msg('Add', { id: 'changeCard.type.add', desc: 'Badge label for an add change entry' }), bg: '#e6f7ec', fg: '#1f7a3e', pin: '#2aa66e' },
    edit: { label: msg('Edit', { id: 'changeCard.type.edit', desc: 'Badge label for an edit change entry' }), bg: '#e6f2ff', fg: '#0062c4', pin: '#0078d4' },
    delete: { label: msg('Delete', { id: 'changeCard.type.delete', desc: 'Badge label for a delete change entry' }), bg: '#fde7e9', fg: '#a4262c', pin: '#c0392b' },
});
/**
 * Compact card representing a single change entry — typed as an `add`,
 * `edit` or `delete` operation. Used in lists where each row maps to one
 * mutation (e.g. RZI scenario changes).
 *
 * Events:
 *   - `change-click`  — user clicked the body of the card
 *   - `change-delete` — user clicked the trailing trash icon
 *
 * Both events carry `{ changeId, type }` in `detail`.
 */
let ChangeCard = class ChangeCard extends LitElement {
    constructor() {
        super(...arguments);
        this.changeId = '';
        this.type = 'add';
        this.title = '';
        this.selected = false;
        this.hideDelete = false;
        this._handleCardClick = (e) => {
            const target = e.composedPath()[0];
            if (target?.closest?.('.delete-btn'))
                return;
            this.dispatchEvent(new CustomEvent('change-click', {
                detail: { changeId: this.changeId, type: this.type },
                bubbles: true,
                composed: true,
            }));
        };
        this._handleDeleteClick = (e) => {
            e.stopPropagation();
            this.dispatchEvent(new CustomEvent('change-delete', {
                detail: { changeId: this.changeId, type: this.type },
                bubbles: true,
                composed: true,
            }));
        };
    }
    _renderTrashIcon() {
        return html `<basic-icon name="delete" size="16"></basic-icon>`;
    }
    render() {
        const style = TYPE_STYLES()[this.type];
        return html `
      <div class="layout" @click=${this._handleCardClick}>
        <div class="pin" style="background:${style.pin}"></div>
        <div class="body">
          <div class="header">
            <div class="title-col">
              <div class="title" title=${this.title}>${this.title}</div>
              ${this.subtitle
            ? html `<div class="subtitle" title=${this.subtitle}>
                    ${this.subtitle}
                  </div>`
            : nothing}
            </div>
            <span
              class="type-badge"
              style="background:${style.bg};color:${style.fg}"
            >
              ${style.label}
            </span>
            ${this.hideDelete
            ? nothing
            : html `<button
                  class="delete-btn"
                  type="button"
                  title=${msg('Delete', { id: 'changeCard.action.delete.title', desc: 'Tooltip for the delete button on a change card' })}
                  @click=${this._handleDeleteClick}
                >
                  ${this._renderTrashIcon()}
                </button>`}
          </div>
        </div>
      </div>
    `;
    }
};
ChangeCard.styles = css `
    :host {
      display: block;
      padding: 12px 16px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition: background 0.15s, border-color 0.15s;
      font-family: inherit;
      color: #333;
      box-sizing: border-box;
      user-select: none;
    }

    :host(:hover) {
      background: #f8fbff;
    }

    :host([selected]) {
      background: #e6f2ff;
      border-left-color: #0078d4;
    }

    .layout {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .pin {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-top: 6px;
    }

    .body {
      flex: 1;
      min-width: 0;
    }

    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .title-col {
      flex: 1;
      min-width: 0;
    }

    .title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    .subtitle {
      font-size: 12px;
      color: #777;
      margin-top: 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .type-badge {
      display: inline-flex;
      align-items: center;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 10px;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .delete-btn {
      flex-shrink: 0;
      margin-top: -2px;
      width: 26px;
      height: 26px;
      padding: 0;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: #888;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s, color 0.15s;
    }

    .delete-btn:hover {
      background: #fde7e9;
      color: #a4262c;
    }

    .delete-btn svg {
      width: 16px;
      height: 16px;
    }
  `;
__decorate([
    property({ type: String })
], ChangeCard.prototype, "changeId", void 0);
__decorate([
    property({ type: String })
], ChangeCard.prototype, "type", void 0);
__decorate([
    property({ type: String })
], ChangeCard.prototype, "title", void 0);
__decorate([
    property({ type: String })
], ChangeCard.prototype, "subtitle", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ChangeCard.prototype, "selected", void 0);
__decorate([
    property({ type: Boolean })
], ChangeCard.prototype, "hideDelete", void 0);
ChangeCard = __decorate([
    localized(),
    customElement('change-card')
], ChangeCard);
export { ChangeCard };
//# sourceMappingURL=ChangeCard.js.map